import { useQueryClient } from '@tanstack/react-query';
import * as QuickActions from 'expo-quick-actions';
import { useRouter } from 'expo-router';
import { useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';

import {
  SHARE_LINK_FALLBACK_ROUTE,
  SHARE_LINK_LABELS,
  type ShareLinkKind,
} from '@/constants/shareLink';
import { useOwnShareLink } from '@/hooks/useOwnShareLink';
import { getMyPublicLinkV2QueryOptions } from '@/services/myPublicLinkV2';
import {
  trackExpertLinkShared,
  trackExpertLinkShortcutUsed,
  trackInviteNativeShared,
} from '@/util/analyticsEvents';
import { shareOwnLink } from '@/util/shareLink';

const SHARE_LINK_ACTION_ID = 'share-ask-link';

/**
 * The action that launched the app stays on `QuickActions.initial` for the life of the
 * process, so it is consumed once here rather than on every effect run.
 */
let hasHandledInitialAction = false;

/**
 * Registers the share action on the app icon for anyone with a link to share, titled for their
 * role, and removes it for everyone else. Its `href` is the role's fallback screen; the handler
 * below navigates there itself when it cannot share straight away. The icon is an iOS system
 * symbol, so Android gets none.
 *
 * The action id stays `share-ask-link` across roles so a stale registration from an earlier
 * build is replaced rather than duplicated. Registration waits for the cold-load session
 * restore, so a relaunch does not wipe the action between "no user yet" and the user arriving.
 */
export function useShareLinkQuickActionItems() {
  const { authenticatedUserId, isAuthResolved, user, kind } = useOwnShareLink({ withLink: false });

  useEffect(() => {
    if (!isAuthResolved) {
      return;
    }

    if (kind) {
      QuickActions.setItems([
        {
          id: SHARE_LINK_ACTION_ID,
          title: SHARE_LINK_LABELS[kind].actionTitle,
          icon: Platform.OS === 'ios' ? 'share' : undefined,
          params: { href: SHARE_LINK_FALLBACK_ROUTE[kind] as string },
        },
      ]).catch(() => undefined);

      return;
    }

    if (!authenticatedUserId || user) {
      QuickActions.setItems([]).catch(() => undefined);
    }
  }, [isAuthResolved, kind, authenticatedUserId, user]);
}

/**
 * Opens the system share sheet with the device holder's link the moment the quick action
 * launches the app, the one-step path into a Messages or Mail conversation. Experts share
 * their Ask Primum link, fetched at tap time so a cold launch still has it; LMPs share their
 * referral link with the invite message. If the link cannot be had or the sheet cannot open,
 * it opens the role's fallback screen, so a tap never lands on nothing.
 *
 * This does not use the library's routing hook. That hook re-runs its effect whenever the
 * callback changes and treats any truthy return as "handled", so an async handler both
 * re-fires the launch action on every re-render and can never fall back. The handler here is
 * stable and reads the session through a ref. Mount it in a layout beneath the root; the secure
 * layout only mounts for a signed-in user, so a cold launch waits for sign-in.
 */
export function useShareLinkQuickActionRouting() {
  const queryClient = useQueryClient();

  const router = useRouter();

  const { authenticatedUserId, kind, shareLink, userName } = useOwnShareLink({ withLink: false });

  const referralUrl = kind === 'referral' ? (shareLink?.url ?? null) : null;

  const sessionRef = useRef({ authenticatedUserId, kind, referralUrl, userName });

  useEffect(() => {
    sessionRef.current = { authenticatedUserId, kind, referralUrl, userName };
  }, [authenticatedUserId, kind, referralUrl, userName]);

  const openFallbackScreen = useCallback(
    (fallbackKind: ShareLinkKind) => {
      setTimeout(() =>
        router.navigate(SHARE_LINK_FALLBACK_ROUTE[fallbackKind], { withAnchor: true })
      );
    },
    [router]
  );

  const handleAction = useCallback(
    async (action: QuickActions.Action) => {
      const {
        authenticatedUserId: userId,
        kind: sessionKind,
        referralUrl: sessionReferralUrl,
        userName: sessionUserName,
      } = sessionRef.current;

      if (action.id !== SHARE_LINK_ACTION_ID || !userId || !sessionKind) {
        return;
      }

      if (sessionKind === 'expert') {
        trackExpertLinkShortcutUsed({ source: 'quick_action' });
      }

      try {
        const url =
          sessionKind === 'expert'
            ? (await queryClient.fetchQuery(getMyPublicLinkV2QueryOptions(userId))).url
            : sessionReferralUrl;

        if (!url) {
          openFallbackScreen(sessionKind);

          return;
        }

        const didShare = await shareOwnLink(sessionKind, url);

        if (sessionKind === 'referral') {
          trackInviteNativeShared({ location: 'quick_action', userName: sessionUserName });
        } else if (didShare) {
          trackExpertLinkShared({
            expertId: Number(userId),
            channel: 'native_share',
            target: 'link',
          });
        }
      } catch {
        openFallbackScreen(sessionKind);
      }
    },
    [queryClient, openFallbackScreen]
  );

  useEffect(() => {
    if (QuickActions.initial && kind && !hasHandledInitialAction) {
      hasHandledInitialAction = true;

      handleAction(QuickActions.initial).catch(() => undefined);
    }

    const subscription = QuickActions.addListener((action) => {
      handleAction(action).catch(() => undefined);
    });

    return () => subscription.remove();
  }, [handleAction, kind]);
}
